import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { fmt, PieChart, BarChart } from './_finHelpers';

// Lease Calculator — mirrors Calculator.net exactly:
// Fixed Rate tab: solve monthly payment from asset value, residual, term, rate.
// Fixed Pay tab: solve the implied interest rate from asset value, residual, term, monthly pay.
export default function LeaseCalc() {
  const [tab, setTab] = useState('rate');
  return (
    <CalcShell showActions={false} look="cnet" title="Lease Calculator"
      subtitle="Compute the monthly payment or the effective interest rate on a lease."
      about={<>
        <p>The <b>Lease Calculator</b> can be used to calculate the monthly payment or the effective interest rate on a lease. If the interest rate is known, use the "Fixed Rate" tab to calculate the monthly payment. If the monthly payment is known, use the "Fixed Pay" tab to calculate the effective interest rate.</p>
        <p>Use the <a href="/calc/auto-lease" className="text-blue-700 underline">Auto Lease Calculator</a> for auto-lease specific calculations for U.S. residents.</p>
      </>}
    >
      <div className="flex gap-1 mb-6 border-b border-slate-200">
        <button onClick={()=>setTab('rate')} data-testid="lease-tab-rate"
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab==='rate'?'border-blue-700 text-blue-700':'border-transparent text-slate-500'}`}>Fixed Rate</button>
        <button onClick={()=>setTab('pay')} data-testid="lease-tab-pay"
          className={`px-4 py-2 text-sm font-semibold border-b-2 -mb-px ${tab==='pay'?'border-blue-700 text-blue-700':'border-transparent text-slate-500'}`}>Fixed Pay</button>
      </div>
      {tab === 'rate' ? <FixedRate/> : <FixedPay/>}
    </CalcShell>
  );
}

// Monthly = depreciation + finance charge
// depreciation = (Asset − Residual) / N
// finance      = (Asset + Residual) × moneyFactor,  moneyFactor = APR / 2400
function computeMonthly(asset, residual, months, ratePct) {
  const dep = (asset - residual) / months;
  const mf  = ratePct / 2400;
  const fin = (asset + residual) * mf;
  return dep + fin;
}

function FixedRate() {
  const [asset, setAsset] = useState(20000);
  const [residual, setResidual] = useState(8000);
  const [years, setYears] = useState(3);
  const [months, setMonths] = useState(0);
  const [rate, setRate] = useState(6);

  const c = useMemo(() => {
    const n = Math.max(1, years * 12 + months);
    const monthly = computeMonthly(asset, residual, n, rate);
    const totalPaid = monthly * n;
    const interest = totalPaid - (asset - residual);
    return { n, monthly, totalPaid, interest };
  }, [asset, residual, years, months, rate]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div>
        <div className="field"><label>Asset Value</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={asset} onChange={e=>setAsset(+e.target.value||0)} className="flex-1" data-testid="lease-asset"/></div></div>
        <div className="field"><label>Residual Value</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={residual} onChange={e=>setResidual(+e.target.value||0)} className="flex-1"/></div></div>
        <div className="field"><label>Lease Term</label>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex gap-2"><input type="number" value={years} onChange={e=>setYears(+e.target.value||0)}/><span className="self-center text-slate-500">years</span></div>
            <div className="flex gap-2"><input type="number" value={months} onChange={e=>setMonths(+e.target.value||0)}/><span className="self-center text-slate-500">months</span></div>
          </div>
        </div>
        <div className="field"><label>Interest Rate</label><div className="flex gap-2"><input type="number" step="0.01" value={rate} onChange={e=>setRate(+e.target.value||0)} className="flex-1"/><span className="self-center text-slate-500">%</span></div></div>
      </div>
      <div>
        <div className="cnet-result-hdr">Result</div>
        <div className="cnet-result-body">
          <table className="w-full text-sm mb-4">
            <tbody>
              <tr className="border-b"><td className="py-2 font-semibold">Monthly Pay</td><td className="text-right font-bold text-lg" data-testid="lease-monthly">{fmt(c.monthly)}</td></tr>
              <tr className="border-b"><td className="py-2">Total of {c.n} Monthly Payments</td><td className="text-right">{fmt(c.totalPaid)}</td></tr>
              <tr><td className="py-2">Total Interest</td><td className="text-right text-orange-600">{fmt(c.interest)}</td></tr>
            </tbody>
          </table>
          <PieChart parts={[
            { label: 'Principal', value: asset - residual, color: '#2563eb' },
            { label: 'Interest',  value: c.interest,      color: '#84cc16' },
          ]} size={180}/>
          {/* Monthly payment breakdown bar graph */}
          <div className="mt-4">
            <h4 className="text-sm font-semibold text-slate-700 mb-2">Monthly Payment Breakdown</h4>
            <BarChart
              data={[
                { label: 'Total', values: [
                  { value: (asset - residual) / c.n, color: '#2563eb', label: 'Depreciation' },
                  { value: c.monthly - (asset - residual) / c.n, color: '#84cc16', label: 'Finance Charge' },
                ]}
              ]}
              stack={true}
              legend={[
                { label: 'Depreciation',   color: '#2563eb' },
                { label: 'Finance Charge', color: '#84cc16' },
              ]}
            />
          </div>
        </div>
      </div>
    </div>
  );
}

function FixedPay() {
  const [asset, setAsset] = useState(20000);
  const [residual, setResidual] = useState(8000);
  const [years, setYears] = useState(3);
  const [months, setMonths] = useState(0);
  const [monthly, setMonthly] = useState(405.06);

  const c = useMemo(() => {
    const n = Math.max(1, years * 12 + months);
    // Solve for money-factor: monthly = (A-R)/n + (A+R)*mf  →  mf = (monthly − (A-R)/n) / (A+R)
    const dep = (asset - residual) / n;
    const finPortion = monthly - dep;
    const mf = (asset + residual) > 0 ? finPortion / (asset + residual) : 0;
    const ratePct = mf * 2400;
    const totalPaid = monthly * n;
    const interest = totalPaid - (asset - residual);
    return { n, mf, ratePct, totalPaid, interest };
  }, [asset, residual, years, months, monthly]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
      <div>
        <div className="field"><label>Asset Value</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={asset} onChange={e=>setAsset(+e.target.value||0)} className="flex-1"/></div></div>
        <div className="field"><label>Residual Value</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" value={residual} onChange={e=>setResidual(+e.target.value||0)} className="flex-1"/></div></div>
        <div className="field"><label>Lease Term</label>
          <div className="grid grid-cols-2 gap-2">
            <div className="flex gap-2"><input type="number" value={years} onChange={e=>setYears(+e.target.value||0)}/><span className="self-center text-slate-500">years</span></div>
            <div className="flex gap-2"><input type="number" value={months} onChange={e=>setMonths(+e.target.value||0)}/><span className="self-center text-slate-500">months</span></div>
          </div>
        </div>
        <div className="field"><label>Monthly Pay</label><div className="flex gap-2"><span className="self-center text-slate-500">$</span><input type="number" step="0.01" value={monthly} onChange={e=>setMonthly(+e.target.value||0)} className="flex-1" data-testid="lease-pay"/></div></div>
      </div>
      <div>
        <div className="cnet-result-hdr">Result</div>
        <div className="cnet-result-body">
          <table className="w-full text-sm mb-4">
            <tbody>
              <tr className="border-b"><td className="py-2 font-semibold">Interest Rate (APR)</td><td className="text-right font-bold text-lg" data-testid="lease-rate">{c.ratePct.toFixed(3)}%</td></tr>
              <tr className="border-b"><td className="py-2">Money Factor</td><td className="text-right">{c.mf.toFixed(6)}</td></tr>
              <tr className="border-b"><td className="py-2">Total of {c.n} Monthly Payments</td><td className="text-right">{fmt(c.totalPaid)}</td></tr>
              <tr><td className="py-2">Total Interest</td><td className="text-right text-orange-600">{fmt(c.interest)}</td></tr>
            </tbody>
          </table>
          <PieChart parts={[
            { label: 'Principal', value: asset - residual, color: '#2563eb' },
            { label: 'Interest',  value: c.interest,      color: '#84cc16' },
          ]} size={180}/>
        </div>
      </div>
    </div>
  );
}
